import { AudioPlayer } from "@components/notebook/note/AudioPlayer";
import NoteCreatedAt from "@components/notebook/note/NoteCreatedAt";
import NoteStatus, {
  getNoteStatusList,
} from "@components/notebook/note/NoteStatus";
import PatientTitle from "@components/notebook/PatientTitle";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDisplayName } from "@hooks/use-display-name";
import Twemoji from "@src/Twemoji";
import { truncate } from "@src/utils";
import React from "react";
import { useNavigate } from "react-router";

import { DSNoteItem } from "../../design-system/organisms/DSNoteItem";

interface NoteItemProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  note: Record<string, any>;
  dataTestIdUuid?: string;
  dataTestIdTemplate?: string;
  dataTestIdNoteTitle?: string;
  hideNoteStatus?: boolean;
  dashboard?: boolean;
  clickable?: boolean;
  handleActionWithPrevention?: (fn: () => void) => void;
  shouldShowAudioPlayer?: boolean;
  href?: string;
}

function NoteItem(props: NoteItemProps) {
  const {
    note,
    dataTestIdUuid,
    dataTestIdTemplate,
    dataTestIdNoteTitle,
    hideNoteStatus = false,
    dashboard = false,
    clickable = true,
    handleActionWithPrevention = (fn: () => void) => fn(),
    shouldShowAudioPlayer = false,
  } = props;
  let { href } = props;
  const navigate = useNavigate();

  href = href || `/edit_note/${note.uuid}`;

  const ownerDisplayName = useDisplayName(note?.user, {
    // @ts-expect-error -- hook type says includeDisplayName but util uses includeLastName
    includeLastName: true,
  });

  function handleNoteItemClick() {
    if (clickable) {
      navigate(href!);
    }
  }

  const noteStatuses = getNoteStatusList(note);

  // Build subtitle — patient/template info on one line, date on the next
  const subtitle = note?.patient?.name ? (
    <>
      <div>
        {note?.jobType ? `${note?.jobTypeName} for ` : "Note for "}
        <PatientTitle
          sex={note?.patient?.sex}
          species={note?.patient?.species}
          name={note?.patient?.name}
          patientUUID={note?.patient?.uuid}
          lastNameOfOwner={note?.patient?.lastNameOfOwner}
          size="sm"
        />
      </div>
      <NoteCreatedAt createdAt={note?.createdAt} size="sm" />
    </>
  ) : (
    <>
      <div data-testid={dataTestIdTemplate}>
        {note?.jobTypeName || "Quicknote"}
      </div>
      <NoteCreatedAt createdAt={note?.createdAt} size="sm" />
    </>
  );

  // Build title icon
  const titleIcon =
    note?.title &&
    (note?.isMadeWithScribephone ? (
      <FontAwesomeIcon
        icon={faPhone}
        className="mr-1 mb-px text-xs text-success shrink-0"
      />
    ) : (
      <Twemoji emoji="📝" />
    ));

  // Build status slot
  const statusSlot = !hideNoteStatus && <NoteStatus note={note} />;

  // Build footer (audio player)
  const footer = shouldShowAudioPlayer && note?.audioStorageLink && (
    <AudioPlayer
      noteUuid={note?.uuid}
      shouldShowDeleteButton={false}
      shouldShowAddButton={false}
      dataTestId={`audio-player-${note?.uuid}`}
    />
  );

  return (
    <DSNoteItem
      title={truncate(note?.title, 64) || ""}
      templateName={note?.jobTypeName || "Quicknote"}
      date=""
      dataTestId={dataTestIdUuid || `note-item-${note?.uuid}`}
      onClick={
        clickable
          ? () => handleActionWithPrevention(handleNoteItemClick)
          : undefined
      }
      href={clickable ? href : undefined}
      subtitle={subtitle}
      statusSlot={statusSlot}
      titleIcon={titleIcon}
      footer={footer}
      ownerName={!dashboard ? ownerDisplayName : undefined}
      noteStatuses={noteStatuses}
      dataTestIdTitle={dataTestIdNoteTitle}
      hideTitle={!note?.title}
    />
  );
}

export default NoteItem;
